"use client";

import Image from "next/image";
import type { ComponentType, SVGProps } from "react";
import { BadgeCheck, ChartNoAxesCombined, ChevronLeft, ChevronRight, LineChart, ShieldCheck } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import FairValuePreview from "@/app/broking/icm 3.0/shared/fair-value-preview";
import { DirectPaths } from "@/config/routes";
import avatarNidhi from "@/app/assets/images/testimonials/avatar-nidhi.png";
import fairValueCardBg from "@/app/assets/images/icm-3.0/fair-value-card-bg.png";
import useNavigation from "@/app/hooks/use-navigation";

type FeatureCard = {
    title: string;
    description?: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

/** Figma variants 11–14; peach bg + border on hover */
const FEATURES: readonly FeatureCard[] = [
    {
        title: "3-stage growth model",
        description: "Fast, steady growth — modelled like analysts value.",
        Icon: LineChart,
    },
    {
        title: "P/E check + 4 valuation multiples",
        description: "See how cheap or pricey the stock is across multiples.",
        Icon: ChartNoAxesCombined,
    },
    {
        title: "Plain-English verdict",
        description: "No jargon — just 'cheap', 'fair', or 'expensive'.",
        Icon: BadgeCheck,
    },
    {
        title: "100% free, no signup",
        description: "Try it instantly. Log in only if you want the advanced tools.",
        Icon: ShieldCheck,
    },
] as const;

const TRUST_AVATARS = [
    { src: avatarNidhi, alt: "Investor" },
    { src: avatarNidhi, alt: "Investor" },
    { src: avatarNidhi, alt: "Investor" },
] as const;

const FeatureCardItem = ({
    title,
    description,
    Icon,
    className = "",
}: FeatureCard & { className?: string }) => (
    <article
        className={`group relative flex h-full flex-col gap-[11px] overflow-hidden rounded-2xl border border-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)] bg-white p-4 transition-[border-color,box-shadow] duration-200 hover:border-[#FDE7D7] hover:shadow-[0_8px_24px_rgba(241,93,42,0.08)] md:p-5 ${className}`.trim()}
    >
        <div
            className="pointer-events-none absolute inset-0 bg-[length:55%_auto] bg-[position:right_top] bg-no-repeat opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            style={{ backgroundImage: `url('${fairValueCardBg.src}')` }}
            aria-hidden
        />
        <Icon
            className="relative size-5 shrink-0 text-brand-primary-500"
            strokeWidth={1.75}
            aria-hidden
        />
        <h3 className="relative text-base font-bold leading-6 text-[var(--colors-text-header,#1A1A1A)] md:text-lg md:leading-7">
            {title}
        </h3>
        {description ? (
            <p className="relative text-sm font-regular leading-5 text-[var(--colors-text-body-text-1,#767676)]">
                {description}
            </p>
        ) : null}
    </article>
);

const TrustRow = ({ className = "" }: { className?: string }) => (
    <div className={`flex w-full items-center gap-3 ${className}`.trim()}>
        <div className="flex shrink-0 -space-x-2" aria-hidden>
            {TRUST_AVATARS.map((avatar, index) => (
                <Image
                    key={`${avatar.alt}-${index}`}
                    src={avatar.src}
                    alt=""
                    width={36}
                    height={36}
                    className="size-9 rounded-full border-2 border-white object-cover"
                />
            ))}
        </div>
        <div className="flex min-w-0 flex-col">
            <p className="text-sm font-bold leading-5 text-[var(--colors-text-header,#1A1A1A)]">
                Trusted by 50,000+ investors
            </p>
            <p className="text-xs font-regular leading-4 text-[var(--colors-text-body-text-1,#767676)]">
                Making smarter decisions everyday
            </p>
        </div>
    </div>
);

const MobileFeatureCarousel = () => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: "start",
        containScroll: "trimSnaps",
        dragFree: false,
    });
    const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
        useNavigation(emblaApi);

    return (
        <div className="flex w-full flex-col gap-5">
            <div className="overflow-hidden" ref={emblaRef}>
                <ul className="flex touch-pan-y gap-4">
                    {FEATURES.map((feature) => (
                        <li
                            key={feature.title}
                            className="min-w-0 shrink-0 grow-0 basis-[min(280px,85%)]"
                        >
                            <FeatureCardItem
                                {...feature}
                                className="shadow-[0_8px_24px_rgba(15,23,42,0.06)]"
                            />
                        </li>
                    ))}
                </ul>
            </div>

            <div className="flex items-center justify-center gap-3">
                <button
                    type="button"
                    onClick={onPrevButtonClick}
                    disabled={prevBtnDisabled}
                    aria-label="Previous feature"
                    className="inline-flex size-10 items-center justify-center rounded-lg border border-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)] bg-white text-[var(--colors-text-header,#1A1A1A)] transition-opacity disabled:opacity-40"
                >
                    <ChevronLeft className="size-5" aria-hidden />
                </button>
                <button
                    type="button"
                    onClick={onNextButtonClick}
                    disabled={nextBtnDisabled}
                    aria-label="Next feature"
                    className="inline-flex size-10 items-center justify-center rounded-lg border border-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)] bg-white text-[var(--colors-text-header,#1A1A1A)] transition-opacity disabled:opacity-40"
                >
                    <ChevronRight className="size-5" aria-hidden />
                </button>
            </div>
        </div>
    );
};

/**
 * Fair Value Calculator marketing section — feature grid + preview card.
 * Mobile-only carousel; desktop layout unchanged.
 */
const FairValueCalculatorSection = () => {
    return (
        <section
            aria-labelledby="fair-value-calc-heading"
            className="w-full bg-white px-4 py-10 md:px-[120px] md:py-[120px]"
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 md:gap-10">
                <header className="flex w-full flex-col items-center gap-1 text-center md:items-start md:gap-2 md:text-left">
                    <p className="font-['Inter'] text-[16px] font-semibold uppercase leading-[22px] tracking-[2px] text-[#F15D2A] md:text-xl md:leading-[26px] md:tracking-[1.6px]">
                        Free tool. Fair value calculator
                    </p>
                    <h2
                        id="fair-value-calc-heading"
                        className="text-2xl font-bold leading-8 text-[var(--colors-text-header,#1A1A1A)] md:text-5xl md:leading-none"
                    >
                        Built for every kind of investor.
                    </h2>
                </header>

                {/* ——— Mobile only ——— */}
                <div className="flex w-full flex-col gap-8 md:hidden">
                    <div className="mx-auto flex w-full max-w-[361px] flex-col gap-6">
                        <FairValuePreview ctaHref={DirectPaths.CALCULATOR} />
                        <TrustRow />
                    </div>
                    <MobileFeatureCarousel />
                </div>

                {/* ——— Desktop only (unchanged) ——— */}
                <div className="hidden w-full items-stretch justify-between md:flex">
                    <ul className="grid w-[min(100%,560px)] grid-cols-2 grid-rows-2 gap-5 self-stretch">
                        {FEATURES.map((feature) => (
                            <li key={feature.title} className="min-h-0">
                                <FeatureCardItem {...feature} />
                            </li>
                        ))}
                    </ul>

                    <div className="flex w-[420px] shrink-0 flex-col gap-6 self-stretch">
                        <FairValuePreview ctaHref={DirectPaths.CALCULATOR} />
                        <TrustRow />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FairValueCalculatorSection;
